import { FormBlock } from '@qbe/ads-blocks-forms';
import { Dropdown, TextField } from '@qbe/ads-core';
import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { v4 } from 'uuid';

import CurrencyField from '~/components-ds/CurrencyField';
import Modal from '~/components-ds/Modal';
import { COVER_CATEGORIES, SPECIFIED_ITEM_LIMIT } from '~/constants/domain-specific';
import { useAppContent, useBusinessDetails, useCoverDetails, useFieldValidation } from '~/hooks';
import { useTrackOptimizeActivate } from '~/hooks/useTrackOptimizeActivate';
import { updateSpecifiedItems } from '~/slices/coverDetails';
import { Cover, CoverItem, CoverTypes } from '~/types/cover';
import { TextFieldState } from '~/types/enums';
import { CurrencyFormat, currencyFormatter } from '~/utils/currencyFormatter';

interface UpdateSpecifiedItemModalProps {
  serialNumber: CoverTypes;
  selectedItem?: CoverItem;
  onUpdate: typeof updateSpecifiedItems;
  onClose: () => void;
}

const UpdateSpecifiedItemModal: React.FC<UpdateSpecifiedItemModalProps> = ({
  serialNumber,
  selectedItem,
  onUpdate,
  onClose,
}) => {
  const { getTextContent: getSharedContent } = useAppContent({ basePath: 'shared' });
  const { getTextContent } = useAppContent({
    basePath: 'dsme.coverDetailsPortableItemsAddItemsModal',
    tokens: {
      specifiedItemLimit: () => currencyFormatter(specifiedItemLimit - 1, CurrencyFormat.Comma),
    },
  });

  const dispatch = useDispatch();
  const { specifiedItems } = useCoverDetails();
  const { industry } = useBusinessDetails();
  const [coverCategory, setCoverCategory] = useState(selectedItem?.category || '');
  const [coverDescription, setCoverDescription] = useState<string | null>(
    selectedItem?.description || ''
  );
  const [coverValue, setCoverValue] = useState<string | null>(selectedItem?.value.toString() || '');
  const [coverError, setCoverError] = useState(false);
  const [coverDescriptionError, setCoverDescriptionError] = useState(false);

  // Tracks the "optimize.activate" event when the component mounts or re-renders.
  // This is used to notify Webtrends (or other analytics tools) for visibility tracking
  // and ensures dynamic content is properly handled.
  useTrackOptimizeActivate();

  const specifiedItemLimit = industry?.limits?.specifiedItemMin || SPECIFIED_ITEM_LIMIT;

  const { debouncedChangeHandler, blurHandler, focusHandler, textFieldState } = useFieldValidation({
    validations: {
      alphaOrNumericWithSpaces: true,
      required: true,
      minLength: 3,
      maxLength: 60,
    },
    onValid: setCoverDescription,
    onError: setCoverDescriptionError,
  });
  const onChangeHandler = (e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    const { value } = e.target;
    setCoverDescription(value);
    debouncedChangeHandler(value);
  };

  const handleSetCoverCategory = (v: string) => {
    setCoverCategory(v);
  };

  /**
   * @TODO
   * move add and edit into redux
   */

  const handleUpdate = (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault();
    const id = selectedItem?.id || v4();
    const code = COVER_CATEGORIES.find((o) => o.description === coverCategory)?.id || '';
    const categoryItem = {
      id,
      code,
      category: coverCategory,
      description: coverDescription ?? undefined,
      value: parseInt(coverValue || '', 10),
      serialNumber,
    };
    let updatedCategoryList: CoverItem[];
    if (selectedItem && specifiedItems) {
      updatedCategoryList = specifiedItems.map((item) =>
        item.id === selectedItem.id ? categoryItem : item
      );
    } else {
      updatedCategoryList = specifiedItems ? [...specifiedItems, categoryItem] : [categoryItem];
    }
    if (updatedCategoryList) dispatch(onUpdate(updatedCategoryList));

    onClose();
  };

  const [touchedCoverCategory, setTouchedCoverCategory] = useState(false);

  return (
    <Modal
      open
      title={getTextContent('title')}
      id="update-specified-item-modal"
      primaryBtnProps={{
        label: getSharedContent('updateButtonLabel'),
        'data-track': 'Update - cover item',
        onClick: handleUpdate,
        disabled:
          !coverCategory || !coverValue || !coverDescription || coverError || coverDescriptionError,
      }}
      secondaryBtnProps={{
        'data-track': 'Cancel - cover item',
        onClick: onClose,
        label: getSharedContent('cancelModalButtonLabel'),
      }}
      contentMaxHeight="340px" // To hide modal overflow scroll bar
    >
      <>
        <FormBlock preset="dropdown" maxWidth="100%">
          <Dropdown
            optionList={COVER_CATEGORIES.map((c) => ({
              label: c.description,
              value: c.description,
              'data-track': 'Add items',
              'data-track-value': 'capture',
            }))}
            label={getTextContent('fields.categoryDropdown.label')}
            id="coverCategory"
            onChange={(e) => {
              handleSetCoverCategory(e.target.value as Cover);
            }}
            onBlur={(e) => {
              handleSetCoverCategory(e.target.value as Cover);
              setTouchedCoverCategory(true);
            }}
            error={!coverCategory && touchedCoverCategory}
            helperText={
              !coverCategory && touchedCoverCategory
                ? getTextContent('fields.categoryDropdown.invalidText')
                : getTextContent('fields.categoryDropdown.helperText')
            }
            defaultValue={selectedItem?.category}
            required
          />
        </FormBlock>

        <FormBlock preset="input" maxWidth="100%">
          <TextField
            onChange={onChangeHandler}
            onBlur={(e) => blurHandler(e.target.value)}
            onFocus={focusHandler}
            id="itemDescription"
            name="itemDescription"
            label={getTextContent('fields.descriptionInput.label')}
            data-track="Item description"
            data-track-value="mask"
            helperText={
              textFieldState === TextFieldState.Error
                ? getTextContent('fields.descriptionInput.invalid')
                : getTextContent('fields.descriptionInput.helperText')
            }
            error={textFieldState === TextFieldState.Error}
            displayIcon
            value={coverDescription ?? ''}
            required
          />
        </FormBlock>
        <FormBlock preset="input" maxWidth="100%" paddingBottom="none">
          <CurrencyField
            id="categoryValue"
            name="category value"
            label={getTextContent('fields.totalValueInput.label')}
            data-track="category value"
            data-track-value="mask"
            helperText={getTextContent('fields.totalValueInput.helperText')}
            errorText={getTextContent('fields.totalValueInput.invalid')}
            defaultValue={coverValue ?? undefined}
            fieldValidation={{
              validations: {
                numericOnly: true,
                required: true,
                minValue: specifiedItemLimit,
              },
              onValid: setCoverValue,
              onError: setCoverError,
            }}
            required
          />
        </FormBlock>
      </>
    </Modal>
  );
};

export default UpdateSpecifiedItemModal;
